import React from "react";
import {
  Layers,
  Calendar,
  Clock,
  Tag,
  Users,
  Pen,
  History,
} from "lucide-react";
import { useParams } from "react-router-dom";
import { useGetPlanDetail, useGetPlanDetailByTab } from "../../hooks";

const InfoCard = () => {
  const { id } = useParams();
  const { data: planDetailResponse } = useGetPlanDetail(id);
  const planData = planDetailResponse?.result || {};

  return (
    <div className="bg-white rounded-lg border shadow-sm relative overflow-hidden flex flex-col md:flex-row mt-6">
      {/* Top Gradient Border */}
      <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-green-400 via-yellow-400 to-orange-400" />

      {/* Left Section */}
      <div className="p-6 flex-1 flex gap-5 mt-1">
        {/* Icon */}
        <div className="w-16 h-16 rounded-2xl bg-blue-50/80 flex items-center justify-center shrink-0">
          <Layers className="w-8 h-8 text-blue-700" strokeWidth={1.5} />
        </div>

        {/* Text Content */}
        <div className="flex flex-col w-full">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
              {planData.planCode || "N/A"}
            </span>
            <span className="text-gray-300">•</span>
            <span className="text-xs text-gray-500">
              {planData.version || "v1.0"}
            </span>
            <span
              className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${planData.statusDisplay === "DRAFT" ? "bg-orange-50 text-orange-700 border-orange-200" : "bg-blue-50 text-blue-700 border-blue-100"} border`}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${planData.statusDisplay === "DRAFT" ? "bg-orange-500" : "bg-blue-600"} mr-1.5`}
              ></span>
              {planData.statusDisplay || "Scheduled"}
            </span>
          </div>

          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {planData.planName || "Plan Name"}
          </h1>

          <p className="text-sm text-gray-500 mb-4 max-w-lg leading-relaxed">
            {planData.description || "No description provided."}
          </p>

          {/* Tags */}
          <div className="flex flex-wrap gap-2.5">
            {(planData.categories || []).map((cat: any, i: number) => (
              <span
                key={`cat-${i}`}
                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-purple-50 text-purple-600 border border-purple-200"
              >
                {cat.name}
              </span>
            ))}
            {(planData.subCategories || []).map((sub: any, i: number) => (
              <span
                key={`sub-${i}`}
                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-gray-50 text-gray-700 border border-gray-200"
              >
                {sub.name}
              </span>
            ))}
            {(planData.utilityServices || []).map(
              (service: string, i: number) => (
                <span
                  key={`svc-${i}`}
                  className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-cyan-50 text-cyan-700 border border-cyan-200"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 mr-1.5"></span>
                  {service}
                </span>
              ),
            )}
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="hidden md:block w-px bg-gray-100 my-6" />

      {/* Right Section */}
      <div className="p-6 md:w-[480px] shrink-0 mt-1">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          <StatBox
            icon={<Calendar className="w-3.5 h-3.5" />}
            label="Effective From"
            value={planData.effectiveDate || "N/A"}
          />
          <StatBox
            icon={<Clock className="w-3.5 h-3.5" />}
            label="Effective To"
            value={planData.endDate || "N/A"}
          />
          <StatBox
            icon={<Tag className="w-3.5 h-3.5" />}
            label="Rates"
            value={planData.ratesCount?.toString() || "0"}
          />
          <StatBox
            icon={<Users className="w-3.5 h-3.5" />}
            label="Consumers"
            value={planData.consumersCount?.toString() || "0"}
            valueClass="text-green-700"
          />
          <StatBox
            icon={<Pen className="w-3.5 h-3.5" />}
            label="Created By"
            value={planData.createdBy || "N/A"}
          />
          <StatBox
            icon={<History className="w-3.5 h-3.5" />}
            label="Updated On"
            value={planData.updatedAt || "N/A"}
          />
        </div>
      </div>
    </div>
  );
};

const StatBox = ({
  icon,
  label,
  value,
  valueClass = "text-gray-700",
}: {
  icon: React.ReactNode;
  label: string;
  value: string | React.ReactNode;
  valueClass?: string;
}) => (
  <div className="bg-white border border-gray-100 rounded-lg p-3 shadow-sm shadow-gray-50/50">
    <div className="flex items-center gap-1.5 text-xs text-gray-500 font-semibold mb-1.5 tracking-wide">
      {icon}
      {label}
    </div>
    <div className={`text-[15px] font-semibold ${valueClass}`}>{value}</div>
  </div>
);

export default InfoCard;
